import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  ShieldOutlined as ShieldOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  Refresh as RefreshIcon,
  RuleFolder as RuleFolderIcon
} from '@mui/icons-material';
import {
  checkPolicyGatesForCi,
  createPolicyGate,
  deletePolicyGate,
  evaluatePolicyGates,
  listPolicyGates,
  updatePolicyGate
} from 'api/securityOps';

const borderColor = 'rgba(245, 158, 11, 0.35)';
const cardSx = { border: '1px solid', borderColor, borderRadius: 3, boxShadow: 'none' };
const actionButtonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};

const emptyForm = {
  id: '',
  name: '',
  is_active: true,
  min_severity: 'high',
  max_critical: 0,
  max_high: 5,
  max_total: 100,
  require_no_kev: false,
  require_no_failed_scans: false
};

const parseError = (error, fallback) => {
  const payload = error?.response?.data;
  if (!payload) return error?.message || fallback;
  if (typeof payload === 'string') return payload;
  return payload.detail || fallback;
};

const boolIcon = (value) => (
  <Chip
    size="small"
    label={value ? 'Yes' : 'No'}
    sx={{
      bgcolor: value ? 'rgba(22,163,74,0.12)' : 'rgba(100,116,139,0.16)',
      color: value ? '#166534' : '#334155',
      fontWeight: 600
    }}
  />
);

const AdminPolicyGatesPage = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [error, setError] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [ciCheck, setCiCheck] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listPolicyGates();
      setRows(Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []);
    } catch (err) {
      setError(parseError(err, 'Failed to load policy gates.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeCount = useMemo(() => rows.filter((row) => row.is_active).length, [rows]);

  const onSubmit = async () => {
    if (!form.name.trim()) {
      setError('Gate name is required.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      is_active: form.is_active,
      config: {
        kind: 'ci_gate',
        min_severity: form.min_severity,
        max_critical: Number(form.max_critical || 0),
        max_high: Number(form.max_high || 0),
        max_total: Number(form.max_total || 0),
        require_no_kev: Boolean(form.require_no_kev),
        require_no_failed_scans: Boolean(form.require_no_failed_scans)
      }
    };
    try {
      if (form.id) {
        await updatePolicyGate(form.id, payload);
      } else {
        await createPolicyGate(payload);
      }
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(parseError(err, 'Failed to save policy gate.'));
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    const cfg = row.config || {};
    setForm({
      id: row.id,
      name: row.name || '',
      is_active: Boolean(row.is_active),
      min_severity: String(cfg.min_severity || 'high'),
      max_critical: Number(cfg.max_critical ?? 0),
      max_high: Number(cfg.max_high ?? 5),
      max_total: Number(cfg.max_total ?? 100),
      require_no_kev: Boolean(cfg.require_no_kev),
      require_no_failed_scans: Boolean(cfg.require_no_failed_scans)
    });
  };

  const onDelete = async (row) => {
    setLoading(true);
    setError('');
    try {
      await deletePolicyGate(row.id);
      if (String(form.id) === String(row.id)) {
        setForm(emptyForm);
      }
      await load();
    } catch (err) {
      setError(parseError(err, 'Failed to delete policy gate.'));
    } finally {
      setLoading(false);
    }
  };

  const onEvaluate = async () => {
    setEvaluating(true);
    setError('');
    try {
      const data = await evaluatePolicyGates({ scope: 'global' });
      setEvaluation(data || null);
    } catch (err) {
      setError(parseError(err, 'Failed to evaluate policy gates.'));
    } finally {
      setEvaluating(false);
    }
  };

  const onCiCheck = async () => {
    setEvaluating(true);
    setError('');
    try {
      const data = await checkPolicyGatesForCi({ scope: 'global', fail_status: 412 });
      setCiCheck(data || null);
    } catch (err) {
      const payload = err?.response?.data;
      if (payload) {
        setCiCheck(payload);
      } else {
        setError(parseError(err, 'CI check failed.'));
      }
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1.5}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b', width: 42, height: 42 }}>
                <ShieldOutlinedIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Policy Gates
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Define release gate thresholds and evaluate readiness from current findings.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Reload">
                <span>
                  <IconButton onClick={load} disabled={loading || saving} sx={{ border: '1px solid', borderColor }}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button variant="contained" onClick={onEvaluate} disabled={evaluating || loading} sx={actionButtonSx}>
                {evaluating ? 'Evaluating...' : 'Evaluate Now'}
              </Button>
              <Button variant="outlined" onClick={onCiCheck} disabled={evaluating || loading} sx={{ borderColor: '#f59e0b', color: '#b45309' }}>
                CI Check
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Total Gates
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {rows.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Active Gates
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#166534' }}>
                {activeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Last Evaluation
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: evaluation?.passed ? '#166534' : '#991b1b' }}>
                {evaluation ? (evaluation.passed ? 'PASSED' : 'FAILED') : 'Not run'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Last CI Gate
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: ciCheck?.passed ? '#166534' : ciCheck ? '#991b1b' : '#334155' }}>
                {ciCheck ? (ciCheck.passed ? 'PASS' : 'BLOCK') : 'Not run'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 5 }}>
          <Card sx={cardSx}>
            <CardHeader title={form.id ? 'Edit Policy Gate' : 'Create Policy Gate'} titleTypographyProps={{ variant: 'h6', fontWeight: 700 }} />
            <Divider />
            <CardContent>
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Gate Name"
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Min Severity"
                    value={form.min_severity}
                    onChange={(event) => setForm((prev) => ({ ...prev, min_severity: event.target.value }))}
                  >
                    {['critical', 'high', 'medium', 'low', 'info'].map((item) => (
                      <MenuItem value={item} key={item}>
                        {item.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max Total"
                    value={form.max_total}
                    onChange={(event) => setForm((prev) => ({ ...prev, max_total: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max Critical"
                    value={form.max_critical}
                    onChange={(event) => setForm((prev) => ({ ...prev, max_critical: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Max High"
                    value={form.max_high}
                    onChange={(event) => setForm((prev) => ({ ...prev, max_high: event.target.value }))}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={form.require_no_kev}
                      onChange={(event) => setForm((prev) => ({ ...prev, require_no_kev: event.target.checked }))}
                    />
                    <Typography variant="body2">Require no KEV hit</Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={form.require_no_failed_scans}
                      onChange={(event) => setForm((prev) => ({ ...prev, require_no_failed_scans: event.target.checked }))}
                    />
                    <Typography variant="body2">Require no failed scans</Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={form.is_active}
                      onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                    />
                    <Typography variant="body2">Active</Typography>
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setForm(emptyForm)}
                    disabled={saving || loading}
                    sx={{ borderColor: '#f59e0b', color: '#b45309' }}
                  >
                    Reset
                  </Button>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Button fullWidth variant="contained" onClick={onSubmit} disabled={saving || loading} sx={actionButtonSx}>
                    {saving ? 'Saving...' : form.id ? 'Update Gate' : 'Create Gate'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <Card sx={cardSx}>
            <CardHeader title="Defined Gates" titleTypographyProps={{ variant: 'h6', fontWeight: 700 }} />
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(245, 158, 11, 0.08)' }}>
                    <TableCell>Name</TableCell>
                    <TableCell>Thresholds</TableCell>
                    <TableCell>Flags</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Alert severity="info" sx={{ m: 1 }}>
                          No policy gates found.
                        </Alert>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const cfg = row.config || {};
                      return (
                        <TableRow hover key={row.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Min severity: {(cfg.min_severity || 'high').toUpperCase()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">
                              Critical ≤ {Number(cfg.max_critical ?? 0)}
                            </Typography>
                            <Typography variant="caption" display="block">
                              High ≤ {Number(cfg.max_high ?? 5)}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Total ≤ {Number(cfg.max_total ?? 100)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.6}>
                              {boolIcon(Boolean(cfg.require_no_kev))}
                              {boolIcon(Boolean(cfg.require_no_failed_scans))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {row.is_active ? (
                              <Chip size="small" label="Active" sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 600 }} />
                            ) : (
                              <Chip size="small" label="Inactive" sx={{ bgcolor: 'rgba(220,38,38,0.12)', color: '#991b1b', fontWeight: 600 }} />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => onEdit(row)}>
                                  <RuleFolderIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => onDelete(row)}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>

      {evaluation ? (
        <Card sx={cardSx}>
          <CardHeader
            title="Gate Evaluation"
            subheader={`Critical: ${evaluation.summary?.critical || 0} • High: ${evaluation.summary?.high || 0} • Total: ${evaluation.summary?.total_findings || 0}`}
            titleTypographyProps={{ variant: 'h6', fontWeight: 700 }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={1}>
              {(evaluation.results || []).map((item) => (
                <Box key={item.id} sx={{ border: '1px solid rgba(245,158,11,0.18)', borderRadius: 2, p: 1.2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {item.name}
                    </Typography>
                    {item.passed ? (
                      <Chip size="small" label="Passed" sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 700 }} />
                    ) : (
                      <Chip size="small" label="Failed" sx={{ bgcolor: 'rgba(220,38,38,0.12)', color: '#991b1b', fontWeight: 700 }} />
                    )}
                  </Stack>
                  {item.violations?.length ? (
                    <Stack spacing={0.4} sx={{ mt: 0.8 }}>
                      {item.violations.map((violation) => (
                        <Typography key={violation} variant="caption" color="text.secondary" display="block">
                          • {violation}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.8, display: 'block' }}>
                      No violations.
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
};

export default AdminPolicyGatesPage;
